import React, { useCallback, useEffect, useState } from 'react';
import { Box, ButtonBase, Stack, TableCell, TableRow, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import BasicTable from '../../../components/BasicTable/BasicTable';
import ListToolbar from '../../../components/ListToolbar/ListToolbar';
import FilteredEmptyState from '../../../components/FilteredEmptyState';
import { useListUrlState } from '../../../utilities/hooks/useListUrlState';
import { useLatestRequest } from '../../../utilities/hooks/useLatestRequest';
import ContractDrawer, { ContractSection } from './Contracts/ContractDrawer';
import AssignContractNumbersDialog from './Contracts/AssignContractNumbersDialog';
import { parseStatusFilter, placementText, pricingText, statusText } from './Contracts/contractText';
import { ITableHeadings } from '../../../common/interfaces/common.interface';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { ContractStatusFilterEnum } from '../../../common/enums/invoicing.enum';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { ChildContractDto, ContractStatusCountsDto } from '../../../common/interfaces/invoicing.interface';
import { getBillingSettings, getChildBillingSummary, getContractCounts, getContracts, setPayer } from '../../../api/invoicing.api';
import { getGroupsWithLocationName } from '../../../api/kindergarten.api';

import { palette } from '../../../theme';
export const CONTRACTS_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'common.child', sortable: false, width: '24%' },
  { id: 2, value: '', label: 'contracts.number', sortable: false, width: '16%' },
  { id: 3, value: '', label: 'contracts.payer', sortable: false, width: '18%' },
  { id: 4, value: '', label: 'contracts.columns.pricing', sortable: false, width: '22%' },
  { id: 5, value: '', label: 'common.status', sortable: false }
];

/** The chips, in the order the design reads them: everyone, done, then what is missing. */
const FILTERS: { value: ContractStatusFilterEnum | undefined; count: keyof ContractStatusCountsDto }[] = [
  { value: undefined, count: 'all' },
  { value: ContractStatusFilterEnum.READY, count: 'ready' },
  { value: ContractStatusFilterEnum.NUMBER, count: 'number' },
  { value: ContractStatusFilterEnum.PAYER, count: 'payer' },
  { value: ContractStatusFilterEnum.PRICE, count: 'price' },
  { value: ContractStatusFilterEnum.GROUP, count: 'group' }
];

const BRAND = palette.primary.main;
const MUTED = palette.text.secondary;

/** A fix that happens inside the cell, without opening the row. */
const QuickFix: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <ButtonBase
    onClick={event => {
      // the row behind is a button too; this one does something more specific
      event.stopPropagation();
      onClick();
    }}
    onKeyDown={event => event.stopPropagation()}
    sx={{ color: BRAND, fontSize: 14, p: '4px 8px', ml: -1, borderRadius: 2, '&:hover': { backgroundColor: palette.surface.tint } }}
  >
    {label}
  </ButtonBase>
);

interface ContractsTabProps {
  kindergartenId: string;
  kindergartenName?: string;
  /** The page header owns the button; the dialog lives here, beside the list it changes. */
  assignOpen?: boolean;
  onAssignClose?: () => void;
}

/**
 * Where a child is made invoiceable: a contract number, the parent the invoice is addressed
 * to, and a price. The chips say how many lack each, and every row opens the one panel that
 * fixes all three.
 *
 * The status filter is in the URL, so a link to "everyone missing a price" survives a
 * reload and can be sent to whoever sets up price lists.
 */
const ContractsTab: React.FC<ContractsTabProps> = ({ kindergartenId, kindergartenName, assignOpen, onAssignClose }) => {
  const { t } = useTranslation();
  const list = useListUrlState();
  const { search, page, perPage } = list;
  const status = parseStatusFilter(list.values('status')[0] ?? null);
  const groupIds = list.values('group');

  const [contracts, setContracts] = useState<ChildContractDto[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<ContractStatusCountsDto | null>(null);
  /** Every child, whatever the search and groups: the "of 40" in "12 of 40 children". */
  const [everyone, setEveryone] = useState<number | null>(null);
  const [loader, setLoader] = useState(false);
  // a filter changed twice quickly must not leave the first answer's rows or chips behind
  const beginContracts = useLatestRequest();
  const beginCounts = useLatestRequest();
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [vatApplicable, setVatApplicable] = useState(false);
  const [editing, setEditing] = useState<{ contract: ChildContractDto; focus?: ContractSection } | null>(null);

  const setStatus = (next: ContractStatusFilterEnum | undefined) => list.setValues('status', next ? [next] : []);

  // `?childId=` opens that child's contract, wherever it is in the list: a link from Debts
  // or from an invoice lands on the panel, not on page one with the child to find
  const location = useLocation();
  const navigate = useNavigate();
  const linkedChildId = new URLSearchParams(location.search).get('childId');
  useEffect(() => {
    if (!linkedChildId || editing?.contract.childId === linkedChildId) return;
    let cancelled = false;
    // the summary carries the same contract row the list does, for any child of this kindergarten
    getChildBillingSummary(kindergartenId, linkedChildId)
      .then(result => {
        if (cancelled) return;
        if (result.results?.contract) setEditing({ contract: result.results.contract });
        else throw new Error('not found');
      })
      .catch(() => {
        if (cancelled) return;
        createNotification(MESSAGE_TYPE.ERROR, t('contracts.notFound'));
        dropLinkedChild();
      });
    return () => {
      cancelled = true;
    };
    // only when the link changes; the panel's own state is not a reason to refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, linkedChildId]);

  /** Off the URL once the panel closes; the list's own parameters stay. */
  const dropLinkedChild = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has('childId')) return;
    params.delete('childId');
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true, state: location.state });
  };

  const loadContracts = useCallback(async () => {
    const isCurrent = beginContracts();
    setLoader(true);
    try {
      const result = await getContracts(kindergartenId, {
        page,
        perPage,
        search: search || undefined,
        groupIds: groupIds.length ? groupIds : undefined,
        status
      });
      if (!isCurrent()) return;
      setContracts(result.results ?? []);
      setTotal(result.paginationInfo?.totalItems ?? 0);
    } catch (error) {
      if (isCurrent()) createNotification(MESSAGE_TYPE.ERROR, t('contracts.loadFailed'));
    } finally {
      if (isCurrent()) setLoader(false);
    }
    // the group list is rebuilt from the URL on every render; its joined form is the dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, page, perPage, search, groupIds.join(), status]);

  const loadCounts = useCallback(async () => {
    const isCurrent = beginCounts();
    try {
      const narrowed = !!search || !!groupIds.length;
      const [result, unfiltered] = await Promise.all([
        getContractCounts(kindergartenId, { search: search || undefined, groupIds: groupIds.length ? groupIds : undefined }),
        narrowed ? getContractCounts(kindergartenId) : Promise.resolve(null)
      ]);
      if (!isCurrent()) return;
      setCounts(result.results ?? null);
      setEveryone(unfiltered ? (unfiltered.results?.all ?? null) : (result.results?.all ?? null));
    } catch {
      if (!isCurrent()) return;
      // the list still works without numbers on the chips
      setCounts(null);
      setEveryone(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, search, groupIds.join()]);

  /** After a save the row and the chips have both moved. */
  const refresh = () => {
    loadContracts();
    loadCounts();
  };

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    getGroupsWithLocationName(kindergartenId)
      .then(result => setGroups((result.results ?? []).map((g: any) => ({ id: g.id, name: g.name }))))
      .catch(() => createNotification(MESSAGE_TYPE.ERROR, t('groups.listLoadFailed')));
    getBillingSettings(kindergartenId)
      .then(result => setVatApplicable(!!result.results?.isInVatSystem))
      .catch(() => undefined);
  }, [kindergartenId, t]);

  const setOnlyParentAsPayer = async (contract: ChildContractDto) => {
    const parent = contract.parents[0];
    try {
      await setPayer(kindergartenId, contract.childId, parent.parentId);
      createNotification(MESSAGE_TYPE.SUCCESS, t('contracts.quickFix.payerSet', { name: parent.fullName, child: contract.childFullName }));
      refresh();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('contracts.saveFailed'));
    }
  };

  const ready = counts?.ready ?? 0;
  const all = counts?.all ?? 0;

  return (
    <Box>
      {/* summary: how far along the kindergarten is, and the filters that say what is left */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={3}
        useFlexGap
        flexWrap="wrap"
        sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: '16px 24px', mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontSize: 12, color: MUTED }}>{t('contracts.summary.readyLabel')}</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }} data-testid="contracts-ready-summary">
            {/* "0 of 0" while the counts load would read as a fact about the kindergarten */}
            {counts ? (
              <>
                {ready}{' '}
                <Box component="span" sx={{ fontSize: 16, fontWeight: 400, color: MUTED }}>
                  {t('contracts.summary.ofChildren', { count: all })}
                </Box>
              </>
            ) : (
              '—'
            )}
          </Typography>
        </Box>
        <Box
          role="progressbar"
          aria-label={t('contracts.summary.readyLabel')}
          aria-valuemin={0}
          aria-valuemax={all}
          aria-valuenow={ready}
          sx={{ width: 160, height: 8, borderRadius: 999, backgroundColor: palette.line, overflow: 'hidden', flexShrink: 0 }}
        >
          <Box sx={{ width: all ? `${(ready / all) * 100}%` : 0, height: '100%', backgroundColor: BRAND, borderRadius: 999 }} />
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" role="group" aria-label={t('contracts.filters.ariaLabel')}>
          {FILTERS.map(filter => {
            const active = filter.value === status;
            const label = t(`contracts.filters.${filter.value ?? 'all'}`);
            return (
              <ButtonBase
                key={filter.count}
                aria-pressed={active}
                onClick={() => setStatus(filter.value)}
                sx={{
                  height: 32,
                  px: 1.5,
                  gap: 0.75,
                  borderRadius: 999,
                  fontSize: 14,
                  border: `1px solid ${active ? BRAND : palette.field.border}`,
                  backgroundColor: active ? BRAND : palette.surface.main,
                  color: active ? palette.common.white : 'text.primary'
                }}
              >
                {label}
                {counts && (
                  <Box component="span" sx={{ fontWeight: 600, opacity: active ? 1 : 0.7 }}>
                    {counts[filter.count]}
                  </Box>
                )}
              </ButtonBase>
            );
          })}
        </Stack>
      </Stack>

      <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4 }}>
        <ListToolbar
          search={{ value: search, onChange: list.setSearch, placeholder: t('contracts.searchChildOrNumber') }}
          filters={[
            {
              kind: 'multi',
              key: 'group',
              label: t('common.group'),
              options: groups.map(group => ({ value: group.id, label: group.name })),
              selected: groupIds,
              onChange: values => list.setValues('group', values)
            }
          ]}
          resultText={loader || everyone === null ? undefined : t('contracts.resultCount', { shown: total, count: everyone })}
          onClearAll={() => list.clearFilters(['group'])}
        />

        <BasicTable
          headings={CONTRACTS_TABLE_HEADINGS}
          loader={loader}
          {...list.tableProps(total)}
          emptyState={
            status && !search && !groupIds.length ? (
              <FilteredEmptyState
                message={t(`contracts.emptyFiltered.${status}`)}
                clearLabel={t('contracts.emptyFiltered.showAll')}
                onClear={() => setStatus(undefined)}
              />
            ) : status || search || groupIds.length ? (
              <FilteredEmptyState onClear={() => list.clearFilters(['group', 'status'])} />
            ) : undefined
          }
        >
          {contracts.map(contract => {
            const pricing = pricingText(t, contract);
            const selected = editing?.contract.childId === contract.childId;
            const open = (focus?: ContractSection) => setEditing({ contract, focus });
            return (
              <TableRow
                key={contract.childId}
                hover
                tabIndex={0}
                onClick={() => open()}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                  }
                }}
                sx={{ cursor: 'pointer', ...(selected ? { backgroundColor: palette.surface.tint } : {}) }}
              >
                <TableCell>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{contract.childFullName}</Typography>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>{placementText(t, contract)}</Typography>
                </TableCell>
                <TableCell>
                  {contract.contractNumber ? (
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{contract.contractNumber}</Typography>
                  ) : contract.membershipId ? (
                    <QuickFix label={t('contracts.quickFix.addNumber')} onClick={() => open('number')} />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  {contract.payerFullName ? (
                    contract.payerFullName
                  ) : contract.parents.length === 1 ? (
                    <QuickFix
                      label={t('contracts.quickFix.setPayer', { name: contract.parents[0].fullName })}
                      onClick={() => setOnlyParentAsPayer(contract)}
                    />
                  ) : contract.parents.length > 1 ? (
                    <QuickFix label={t('contracts.quickFix.pickPayer')} onClick={() => open('payer')} />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 14 }}>{pricing.title}</Typography>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>{pricing.detail}</Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {contract.isReadyForInvoicing ? (
                      <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 20, color: 'success.main' }} />
                    ) : (
                      <ErrorOutlineIcon aria-hidden sx={{ fontSize: 20, color: 'warning.main' }} />
                    )}
                    <Typography sx={{ fontSize: 14 }}>{statusText(t, contract)}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </BasicTable>
      </Box>

      {editing && (
        <ContractDrawer
          kindergartenId={kindergartenId}
          contract={editing.contract}
          focus={editing.focus}
          vatApplicable={vatApplicable}
          onClose={() => {
            setEditing(null);
            dropLinkedChild();
          }}
          onSaved={refresh}
        />
      )}

      {assignOpen && (
        <AssignContractNumbersDialog
          kindergartenId={kindergartenId}
          kindergartenName={kindergartenName}
          onClose={() => onAssignClose?.()}
          onDone={refresh}
        />
      )}
    </Box>
  );
};

export default ContractsTab;
